import {
  chakra,
  Flex,
  Icon,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import iconArrowDown from '~/assets/svgs/icon-arrow-down.svg?react'
import { useUserState } from '~/atoms/UserState'
import { useGetUserAgentEventLogs } from '~/queries/queueQueries'
import { DateRange, SortDirection } from '~/types/Shared'

import { formatDateString, formatTimeString } from '@mangovoice/core'

export enum EventLogColumn {
  When = 'when',
  Who = 'who',
  EventName = 'event_name',
  ChangedBy = 'changed_by',
}

interface SortableColumnHeaderProps {
  title: string
  columnType: EventLogColumn
  sortDirection?: SortDirection
  onSortClick?: (
    columnType: EventLogColumn,
    currentSortDirection?: SortDirection,
  ) => void
}

const StickyTh = chakra(Th, {
  baseStyle: {
    position: 'sticky',
    top: '0',
    zIndex: '2',
    padding: '0.75rem 1rem 0.75rem 0rem',
    bg: 'var(--Gray-Gray100, #FAFAFC)',
    _after: {
      content: '""',
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      height: '0.1rem',
      backgroundColor: 'var(--Gray-Gray400, #E5E5EE)',
    },
  },
})

const tableHeader = (title: string) => (
  <StickyTh>
    <Text
      textTransform="none"
      fontSize={'0.75rem'}
      fontWeight={'700'}
      lineHeight={'1rem'}
      color={'var(--Gray-Gray1300, #1D1D30)'}
    >
      {title}
    </Text>
  </StickyTh>
)

const SortableColumnHeader = ({
  title,
  columnType,
  sortDirection,
  onSortClick,
}: SortableColumnHeaderProps) => (
  <StickyTh
    cursor="pointer"
    onClick={() => onSortClick?.(columnType, sortDirection)}
    textTransform="none"
  >
    <Flex alignItems="center" gap="0.5rem">
      <Text
        fontSize={'0.75rem'}
        fontWeight={'700'}
        lineHeight={'1rem'}
        color={'var(--Gray-Gray1300, #1D1D30)'}
      >
        {title}
      </Text>
      <Icon
        as={iconArrowDown}
        transform={`rotate(${
          sortDirection === SortDirection.Ascending ? 180 : 0
        }deg)`}
        transition="transform 0.2s"
        opacity={sortDirection ? 1 : 0.5}
      />
    </Flex>
  </StickyTh>
)

const cellText = (text: string) => {
  return (
    <Text
      overflow={'hidden'}
      textOverflow={'ellipsis'}
      whiteSpace={'nowrap'}
      fontSize={'1rem'}
      fontWeight={'400'}
      lineHeight={'1.5rem'}
      color={'var(--Gray-Gray1300, #1D1D30)'}
    >
      {text}
    </Text>
  )
}

const cell = (children: React.ReactNode) => {
  return (
    <Td
      padding={'0'}
      height={'3.75rem'}
      flexDirection={'column'}
      justifyContent={'center'}
      alignItems={'flex-start'}
      flex={'1 0 0'}
    >
      {children}
    </Td>
  )
}

const textCell = (text: string) => {
  return cell(cellText(text))
}

const formatStartTime = (timestamp: string): string => {
  return `${formatDateString(timestamp)} ${formatTimeString(timestamp)}`
}

interface EventLogTableProps {
  filters: {
    eventTypes: string[]
    details: string[]
  }
  dateTimeRange: DateRange
  initialCreatedAtSort?: SortDirection
  onCreatedAtSortChange?: (sortDirection: SortDirection) => void
}

const EventLogTable = ({
  filters,
  dateTimeRange,
  initialCreatedAtSort,
  onCreatedAtSortChange,
}: EventLogTableProps) => {
  const { pbxUsers, pbxId } = useUserState()

  const [columnSorting, setColumnSorting] = useState<{
    column: EventLogColumn
    direction: SortDirection
  }>({
    column: EventLogColumn.When,
    direction: initialCreatedAtSort ?? SortDirection.Descending,
  })

  const { data: eventLogs, refetch: refetchEventLogs } =
    useGetUserAgentEventLogs({
      params: {
        pbx_id: pbxId,
        event_types: filters.eventTypes,
        details: filters.details,
        start_date: dateTimeRange.after?.toISOString().split('T')[0],
        end_date: dateTimeRange.before?.toISOString().split('T')[0],
        ordering:
          columnSorting.direction === SortDirection.Descending
            ? '-created_at'
            : 'created_at',
      },
    })

  useEffect(() => {
    refetchEventLogs({})
  }, [filters, dateTimeRange, columnSorting])

  const handleSortClick = (
    columnType: EventLogColumn,
    currentSortDirection?: SortDirection,
  ) => {
    setColumnSorting({
      column: columnType,
      direction:
        currentSortDirection === SortDirection.Ascending
          ? SortDirection.Descending
          : SortDirection.Ascending,
    })
    onCreatedAtSortChange?.(
      currentSortDirection === SortDirection.Ascending
        ? SortDirection.Descending
        : SortDirection.Ascending,
    )
  }

  return (
    <Flex
      padding="1.25rem"
      backgroundColor="var(--Gray-Gray100, #FAFAFC)"
      alignSelf="stretch"
      overflow="hidden"
      flexDirection="column"
    >
      <Table variant="simple" style={{ tableLayout: 'fixed', width: '100%' }}>
        <Thead>
          <Tr>
            <SortableColumnHeader
              title="When"
              columnType={EventLogColumn.When}
              sortDirection={columnSorting.direction}
              onSortClick={handleSortClick}
            />
            {tableHeader('Who')}
            {tableHeader('Status')}
            {tableHeader('In Queue')}
            {tableHeader('Changed By')}
          </Tr>
        </Thead>
      </Table>
      <Flex overflowY="auto" flex="1">
        <Table variant="simple" style={{ tableLayout: 'fixed', width: '100%' }}>
          <Tbody>
            {eventLogs?.results?.map((event) => (
              <Tr
                key={event.id}
                height={'4.75rem'}
                borderBottom={'0.1rem solid var(--Gray-Gray400, #E5E5EE)'}
                alignSelf={'stretch'}
              >
                {textCell(formatStartTime(event.created_at))}
                {textCell(
                  pbxUsers?.find((user) => user.id === event.user_id)
                    ?.full_name ?? 'Unknown',
                )}
                {textCell(event.agent_status)}
                {textCell(event.logged_in ? 'Yes' : 'No')}
                {textCell(
                  event.initiated_by_user_id === 20437
                    ? 'System'
                    : (pbxUsers?.find(
                        (user) => user.id === event.initiated_by_user_id,
                      )?.full_name ?? 'Unknown'),
                )}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Flex>
    </Flex>
  )
}

export default EventLogTable
